import { yupResolver } from '@hookform/resolvers/yup';
import InfoIcon from '@mui/icons-material/Info';
import { TextField } from '@mui/material';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import FromModelButton from '../../../FromModelButton';

import { FormKeys, ICommercialModelPartProps } from './interface';
import { ValidationSchema } from './validation';
import './styles.scss';

import Button from '@components/Button';
import { useSnackbar } from '@components/Snackbar/hooks';

export const CommercialModelPart = ({
  category,
  commercialUnitPartNumberValue,
  modelNumber,
  notes,
  oemModel,
  oemModelId,
  partName,
  serialNumber,
  setCommercialUnitPartNumberHasError,
  setCommercialUnitPartNumberValue,
}: ICommercialModelPartProps) => {
  const navigate = useNavigate();
  const { control, watch, formState } = useForm<FormKeys>({
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      partNumber: commercialUnitPartNumberValue,
    },
    mode: 'onChange',
  });

  const partNumberValue = watch('partNumber');

  const handleFindReplacements = () => {
    if (partNumberValue)
      navigate(
        `/bluon-search?tab=searchPart&search=${encodeURI(partNumberValue)}`,
      );
    else navigate('/bluon-search?tab=searchPart');
  };

  useEffect(() => {
    setCommercialUnitPartNumberHasError(!!formState.errors.partNumber);
  }, [formState.errors.partNumber]);

  const title = `Commercial Unit - Find${
    partName ? ` ${partName.substring(0, partName.length - 1)}` : ''
  }${category === 1 ? '' : ' for'}:`;

  return (
    <div className='commercial-model-part-container max-h-[64rem] w-full bg-[var(--bluon--ds--lightgray-4)] p-[3rem]'>
      <div className='mb-[1.5rem] flex items-center'>
        <InfoIcon className='mr-[0.5rem] text-[1.5rem] text-[var(--bluon--light--orange-1)]' />
        <div className='text-[1.5rem] font-normal leading-[2rem] text-[var(--bluon--ds--black)]'>
          {title}
        </div>
      </div>
      {(category === 3 || category === 2) && (
        <div className='mb-[1.5rem] flex w-full'>
          <div className='w-3/6'>
            <div className='mb-[1.688rem] flex flex-row items-center'>
              <ModelAndSeriesRow
                label='Extended Model #: '
                value={modelNumber}
                copyBtnLabel='Copy Model #'
              />
            </div>
            {category === 3 && (
              <div className='flex flex-row items-center'>
                <ModelAndSeriesRow
                  label='Serial #: '
                  value={serialNumber}
                  copyBtnLabel='Copy Serial #'
                />
              </div>
            )}
          </div>
          <div className='flex w-3/6'>
            {notes && (
              <div className='break-all text-[1rem] font-normal leading-[1.188rem] text-[var(--bluon--ds--black)]'>
                <strong>Notes: </strong>
                {notes}
              </div>
            )}
          </div>
        </div>
      )}
      {category === 1 && (
        <div className='mb-[1.5rem] flex w-full'>
          <FromModelButton
            buttonAdditionalStyle='bg-white'
            labelAdditionalStyle='text-[0.813rem]'
            brand=''
            model={oemModel}
            modelId={oemModelId}
          />
        </div>
      )}
      <div className='part-number-container flex h-[3.5rem] w-full items-start'>
        <div className='part-number mr-[1rem]'>
          <Controller
            name='partNumber'
            control={control}
            render={({ field, fieldState, formState }) => (
              <TextField
                sx={{
                  background: '#fff',
                  label: {
                    color: 'rgba(0, 0, 0, 0.60)',
                    fontSize: '16px',
                    lineHeight: '24px',
                    fontWeight: 'normal',
                  },
                  input: {
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '16px',
                    lineHeight: '24px',
                  },
                  '.MuiFormHelperText-root': {
                    backgroundColor: '#f0f0f0',
                    margin: 0,
                    paddingTop: '3px',
                    paddingLeft: '14px',
                  },
                }}
                id='partNumber'
                data-testid='partNumber-text-field'
                variant='outlined'
                label='Enter Part Number'
                name={field.name}
                onBlur={field.onBlur}
                onChange={(e) => {
                  field.onChange(e);
                  setCommercialUnitPartNumberValue(e.target.value);
                }}
                defaultValue={field.value}
                error={!!fieldState.error}
                fullWidth
                autoComplete='off'
                helperText={formState.errors.partNumber?.message}
              />
            )}
          />
        </div>
        <div className='mt-1.5 w-[19.688rem]'>
          <Button
            className={classNames('w-full !py-2', {
              '!bg-[var(--bluon--ds--darkblue)]': !formState.errors.partNumber,
            })}
            onClick={handleFindReplacements}
            disabled={!!formState.errors.partNumber}
          >
            <div
              className={classNames(
                'text-[0.938rem] font-medium uppercase leading-[1.625rem] tracking-[0.029rem]',
                {
                  'text-white': !formState.errors.partNumber,
                  'text-[var(--bluon--ds--lightgray-button-text)]':
                    !!formState.errors.partNumber,
                },
              )}
            >
              Find replacements in bluon search
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

const ModelAndSeriesRow = ({
  label,
  value,
  copyBtnLabel,
}: {
  label: string;
  value: string;
  copyBtnLabel: string;
}) => {
  const { showMessage } = useSnackbar();

  return (
    <>
      <div className='mr-2 text-[1rem] font-normal leading-[1.188rem] tracking-[-0.016rem] text-[var(--bluon--ds--black)]'>
        <strong>{label} </strong>
        {value}
      </div>
      <Button
        className='ml-[0.5rem] h-[2rem] w-[7.625rem] rounded !bg-white !p-0'
        variant='outlined'
        onClick={() => {
          navigator.clipboard.writeText(value || '');
          showMessage(`${value || ''} copied to clipboard`, {
            severity: 'info',
            autoHide: true,
          });
        }}
      >
        <div className='text-[0.813rem] font-medium uppercase leading-[1.375rem] tracking-[0.029rem] text-[var(--bluon--ds--light-blue)]'>
          {copyBtnLabel}
        </div>
      </Button>
    </>
  );
};
